import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { EllipsisVertical, Ellipsis, Share2, Trash2, Edit } from 'lucide-react';

const MenuContainer = styled.div`
  position: absolute;
  right: 3px;
  top: 3px;
  display: inline-block;
`;

const Button = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  background: var(--bg-panel);
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 1001;

  &:hover {
    color: var(--text-primary);
    background: var(--bg-hover);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 30px;
  right: 2px;
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 140px;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-8px);
  transition: all 0.2s ease;

  ${props => props.isOpen && `
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  `}
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }

  &:first-child {
    border-radius: 6px 6px 0 0;
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }

  &:only-child {
    border-radius: 6px;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: var(--border-color);
  margin: 0;
`;

const EllipsisMenu = ({ 
  onShare, 
  onDelete, 
  onEdit,
  size = 16,
  showShare = true,
  showDelete = true,
  showEdit = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const handleButtonClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    if (onShare) {
      onShare();
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    if (onEdit) {
      onEdit();
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    if (onDelete) {
      onDelete();
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <MenuContainer ref={menuRef}>
      <Button onClick={handleButtonClick}>
        {isOpen ? (
          <Ellipsis size={size} />
        ) : (
          <EllipsisVertical size={size} />
        )}
      </Button>
      
      <DropdownMenu isOpen={isOpen}>
        {showEdit && (
          <MenuItem onClick={handleEditClick}>
            <Edit size={14} />
            Edit
          </MenuItem>
        )}
        
        {showEdit && showShare && <MenuDivider />}
        
        {showShare && (
          <MenuItem onClick={handleShareClick}>
            <Share2 size={14} />
            Share
          </MenuItem>
        )}
        
        {(showEdit || showShare) && showDelete && <MenuDivider />}
        
        {showDelete && (
          <MenuItem onClick={handleDeleteClick}>
            <Trash2 size={14} />
            Delete
          </MenuItem>
        )}
      </DropdownMenu>
    </MenuContainer>
  );
};

export default EllipsisMenu; 